<template>
  <PortalLayout section="Alert Templates">
    <div class="mt-4">
      <Loading v-if="isPropertyLoading || isLoading" />
      <div v-else-if="property" class>
        <div v-if="templates && templates.length > 0">
          <AddButton @click="addClick">Add Template</AddButton>
          <div
            v-for="template in templates"
            :key="template.templateId"
            class="px-4 py-2 pt-3 my-2 bg-gray-100 border-t cursor-pointer hover:bg-teal-200"
            @click="editClick(template)"
          >
            {{ template.name }}
          </div>
        </div>
        <div v-else class="mb-2">
          <span>-- No Templates found for this building --</span>
        </div>
        <div>
          <AddButton @click="addClick">Add Template</AddButton>
        </div>

        <EditPopup
          entity-name="Template"
          :is-visible="isPopupVisible"
          :is-edit-mode="isEditing"
          :save-error="saveError"
          :delete-enabled="true"
          :save-on-enter="false"
          :close-on-outside-click="false"
          @close="isPopupVisible = false"
          @save="saveClick"
          @delete="deleteClick"
        >
          <BaseInput
            type="text"
            v-model="templateName"
            label="Name"
            placeholder="Template Name"
            class="mb-2"
            :error="errors.templateName"
          />
          <BaseTextarea
            class="h-64 mb-8"
            v-model="templateText"
            label="Text"
            :allow-resize="false"
            :error="errors.templateText"
          />
        </EditPopup>
      </div>
    </div>
  </PortalLayout>
</template>

<script setup lang="ts">
import { useAlertStore } from "../../store/alertStore";
import { useBldgStore } from "../../store/bldgStore";
import { SaveTemplateRequest, TemplateDTO } from "../../store/generatedFetchClient";

import { useField, useForm } from "vee-validate";
import * as yup from "yup";

const bldgStore = useBldgStore();
const alertStore = useAlertStore();

var isPropertyLoading = computed(() => bldgStore.isLoading);
var property = computed(() => bldgStore.property);
var templates = computed(() => alertStore.templates);
var isLoading = ref(false);

onMounted(loadData);
watch(property, loadData);

function loadData() {
  if (property.value?.portalId) {
    isLoading.value = true;
    alertStore.getTemplates(property.value.portalId);
    isLoading.value = false;
  }
}

// Popup
let isEditing = ref(false);
let isPopupVisible = ref(false);
let saveError = ref("");

const { handleSubmit, errors } = useForm({
  validationSchema: yup.object({
    templateName: yup.string().required().max(50).label("Name"),
    templateText: yup.string().required().label("Text"),
  }),
  initialValues: {
    templateName: "",
    templateText: "",
  },
});

let templateId = ref(-1);
const { value: templateName } = useField<string>("templateName");
const { value: templateText } = useField<string>("templateText");

let saveClick = handleSubmit(onValidSubmit, onInvalidSubmit);

async function onValidSubmit() {
  let saveTemplate = {
    templateId: templateId.value,
    portalId: property.value.portalId,
    name: templateName.value,
    text: templateText.value,
    type: "Alert", // Hardcode type for now; convert to enum later?
  } as SaveTemplateRequest;

  saveError.value = await alertStore.saveTemplate(saveTemplate);
  if (!saveError.value) {
    isPopupVisible.value = false;
    await alertStore.getTemplates(property.value.portalId);
  }
}

function onInvalidSubmit(ctx: any) {
  saveError.value = "One or more invalid form inputs.";
  console.log("error trying to submit form", ctx);
}

let addClick = function () {
  templateId.value = 0;
  templateName.value = "";
  templateText.value = "";

  saveError.value = "";
  isEditing.value = false;
  isPopupVisible.value = true;
};

let editClick = function (selected: TemplateDTO) {
  templateId.value = selected.templateId ?? 0;
  templateName.value = selected.name ?? "";
  templateText.value = selected.template ?? "";

  saveError.value = "";
  isEditing.value = true;
  isPopupVisible.value = true;
};

async function deleteClick() {
  saveError.value = await alertStore.deleteTemplate(
    templateId.value,
    property.value.portalId
  );

  if (!saveError.value) {
    isPopupVisible.value = false;
    await alertStore.getTemplates(property.value.portalId);
  }
}
</script>
